import React, { useEffect, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import DeclarationDrawer from "../../../components/drawers/attendee-side/DeclarationDrawer";
import { ReactComponent as AcceptedSvg } from "../../../assets/images/svgs/acceptedSvg.svg";

import { siteRoutes } from "../../../utils/helpers/enums/routes";
import AttendeeBreadcrumb, {
  AttendeBreadcrumbLink,
} from "../../../components/breadcrumbs/AttendeeBreadcrumb";
import useAuth from "containers/auth/useHooks";
import useAttendee from "../useHooks";
import TermsAndCondition from "components/drawers/host-side/TermsAndCondtion";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

interface FormValues {
  declaration: boolean;
  ambassador?: string;
  ambassadorReferralCode?: string;
}

type AcceptedKeys =
  | "expectations"
  | "media"
  | "liability"
  | "conduct"
  | "checklist";

const DeclarationForm: React.FC = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  const navigate = useNavigate();

  const registeredData = useSelector(
    (state: any) => state.sharedReducer.registeredData
  );

  const [drawerContent, setDrawerContent] = useState<AcceptedKeys | "">("");
  const [open, setOpen] = useState<boolean>(false);
  const [termsDrawerOpen, setTermsDrawerOpen] = useState<boolean>(false);
  const [accepted, setAccepted] = useState<Record<AcceptedKeys, boolean>>({
    expectations: false,
    media: false,
    liability: false,
    conduct: false,
    checklist: false,
  });
  const [showValidationErrors, setShowValidationErrors] =
    useState<boolean>(false);

  const profileCompleted = registeredData?.profiles?.attendee?.profileCompleted;
  useEffect(() => {
    if (profileCompleted) {
      navigate(siteRoutes.attendeeHome);
    }
  }, [profileCompleted]);

  // Dummy state and handler for driverLicenseOrStateId input
  const [formData, setFormData] = useState<{ driverLicenseOrStateId: number }>({
    driverLicenseOrStateId: 0,
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setFormData({ driverLicenseOrStateId: Number(e.target.value) });
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    if (!allAccepted) {
      setShowValidationErrors(true);
      return;
    }
    setShowValidationErrors(false);

    // Prepare API payload
    const apiPayload = {
      ambassadorReferralCode: data.ambassadorReferralCode || "",
    };

    // Call the API
    await postAttendeeDeclaration(apiPayload);
  };

  const handleAccept = (key: string) => {
    setAccepted((prev) => ({ ...prev, [key as AcceptedKeys]: true }));
    setOpen(false);
    // Hide validation errors when user accepts a term
    if (showValidationErrors) {
      setShowValidationErrors(false);
    }
  };

  const allAccepted = Object.values(accepted).every(Boolean);

  const declarationItems: {
    key: AcceptedKeys;
    label: string;
    heading: string;
  }[] = [
    {
      key: "expectations",
      label: "Expectations for learning and behavior",
      heading: "Expectations for learning and Behavior",
    },
    {
      key: "media",
      label: "Media and photograph consent",
      heading: "Media and Photograph Consent",
    },
    {
      key: "liability",
      label: "Liability Disclaimer",
      heading: "Liability Disclaimer",
    },
    { key: "conduct", label: "Code of Conduct", heading: "Code of Conduct" },
    {
      key: "checklist",
      label: "Explorer checklist",
      heading: "Attendee Checklist",
    },
  ];

  const toggleTermsDrawer = () => {
    setTermsDrawerOpen(!termsDrawerOpen);
  };

  const breadcrumbLinks: AttendeBreadcrumbLink[] = [
    { title: "Home", path: siteRoutes.attendeeHome },
    {
      title: "Declaration",
      path: siteRoutes.declarationForm,
    },
  ];

  const { getPrivacyPolicy } = useAuth();
  const { postAttendeeDeclaration } = useAttendee();
  const [declarationContent, setDeclarationContent] = useState<any[]>([]);

  const handleViewClick = async (heading: string) => {
    const content = await getPrivacyPolicy({ heading }, setDeclarationContent);
    return content;
  };

  return (
    <div className="container min-h-screen">
      <AttendeeBreadcrumb links={breadcrumbLinks} />
      <h2 className="text-3xl font-bold mb-6">Declaration</h2>

      <div className="flex items-center gap-4 mb-6">
        {declarationItems.map(({ key, label, heading }) => (
          <div
            key={key}
            onClick={async () => {
              await handleViewClick(heading);
              setDrawerContent(key);
              setOpen(true);
            }}
            className={`flex flex-col justify-center items-center p-3 border-[0.4px] rounded-lg w-[167px] h-[132px] transition-all duration-200 cursor-pointer hover:shadow-md ${
              accepted[key]
                ? "border-green-500 bg-green-50"
                : showValidationErrors && !accepted[key]
                ? "border-red-500 bg-red-50"
                : "border-borderColor hover:border-yellowText"
            }`}
          >
            <p className="text-center text-sm text-BlackText font-medium">
              {label}
            </p>
            <button
              type="button"
              className={`text-sm mt-2 px-3 py-1 rounded-lg transition-colors duration-200 pointer-events-none ${
                accepted[key]
                  ? "text-green-600"
                  : showValidationErrors && !accepted[key]
                  ? "text-red-600 border border-red-300"
                  : "text-yellowText"
              }`}
            >
              {accepted[key] ? <AcceptedSvg /> : "View"}
            </button>
          </div>
        ))}
      </div>

      {/* Validation Error Messages */}
      {showValidationErrors && !allAccepted && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Please accept all terms and conditions
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p className="mb-2">
                  You must accept the following terms before continuing:
                </p>
                <ul className="list-disc list-inside space-y-1">
                  {declarationItems
                    .filter((item) => !accepted[item.key])
                    .map((item, index) => (
                      <li key={index} className="font-medium">
                        {item.label}
                      </li>
                    ))}
                </ul>
                <p className="mt-2 text-xs">
                  Click "View" on each item above to read and accept the terms.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="flex items-start  w-full sm:w-1/2 md:w-2/3 my-3">
          <input
            type="checkbox"
            {...register("declaration", { required: true })}
            className="custom-checkbox mt-[2px]"
            disabled={!allAccepted}
          />
          <label htmlFor="link-checkbox" className="ms-2">
            <p className="text-labelTextColor text-base">
              I hereby declare that I have thoroughly reviewed and understood
              the{" "}
              <span
                onClick={toggleTermsDrawer}
                className="text-yellowText cursor-pointer"
              >
                Terms and policies
              </span>{" "}
              of <span className="text-yellowText">Learn Local app</span>.
            </p>
            <p className="text-labelTextColor text-base">
              I acknowledge and agree to abide by all the rules and regulations
              set forth. By using this app, I commit to adhering to the
              established guidelines.
            </p>
          </label>
        </div>
        {errors.declaration && (
          <p className="text-red-500 text-xs">You must accept all terms.</p>
        )}

        <div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            <div className="flex flex-col gap-1">
              <label className="text-label text-base font-semibold">
                Is there an ambassador that should be thanked for referring you?
                (Optional)
              </label>
              <input
                type="text"
                placeholder="Enter Ambassador ID / Name"
                className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                {...register("ambassadorReferralCode")}
              />
              {errors.ambassadorReferralCode && (
                <p className="text-red-500 text-sm">
                  {errors.ambassadorReferralCode.message}
                </p>
              )}
            </div>
          </div>
        </div>

        <button
          type="submit"
          className={`px-6 py-3 text-white rounded-full font-medium transition-all duration-200 ${
            allAccepted
              ? "bg-primaryBlueText  cursor-pointer"
              : "bg-gray-400 cursor-not-allowed"
          }`}
          disabled={!allAccepted}
        >
          {allAccepted ? "Continue" : "Please accept all terms to continue"}
        </button>
      </form>
      {open && (
        <DeclarationDrawer
          isOpen={open}
          onClose={() => setOpen(false)}
          contentKey={drawerContent}
          onAccept={handleAccept}
          content={declarationContent}
          isAlreadyAccepted={drawerContent ? accepted[drawerContent] : false}
        />
      )}

      {termsDrawerOpen && (
        <TermsAndCondition
          isOpen={termsDrawerOpen}
          onClose={toggleTermsDrawer}
        />
      )}
    </div>
  );
};

export default DeclarationForm;
